import React, { useEffect } from 'react';
import cn from 'classnames';

import { passiveArg } from '../util';
import { useClickOutside, useEscPress } from '../util/hooks';

import Text from './Text';

import RefraccionesLogo from './logos/refracciones';
import BtgLogo from './logos/btg';

import * as styles from './AboutOverlay.module.css';

const CLOSED_ABOUT = 'btg_closedAbout';
const CLOSED_ABOUT_WAIT = 1000 * 60 * 60 * 24 * 30; // 30 days

const hasClosedAbout = () => {
  if (typeof window === 'undefined') return false;
  const last = window.localStorage.getItem(CLOSED_ABOUT);
  return CLOSED_ABOUT_WAIT + parseInt(last) > Date.now();
};

const AboutOverlay = ({ isOpen, setShow, initialShow, text }) => {
  const onClose = () => {
    setShow(false);
    try {
      window.localStorage.setItem(CLOSED_ABOUT, Date.now());
    } catch (e) {
      console.error(e);
    }
  };

  const element = useClickOutside(onClose);

  useEffect(() => {
    setShow(!hasClosedAbout() && initialShow);
  }, [initialShow, setShow]);

  useEffect(() => {
    const closeOnScroll = e => {
      if (window.scrollY > 0 && isOpen) {
        setShow(false);
        document.removeEventListener('scroll', closeOnScroll, passiveArg);
      }
    };
    document.addEventListener('scroll', closeOnScroll, passiveArg);
    return () => {
      document.removeEventListener('scroll', closeOnScroll, passiveArg);
    };
  }, [isOpen, setShow]);

  useEffect(() => {
    if (!isOpen || element.current === null) return;
    element.current.focus({ preventScroll: true });
  }, [isOpen, element]);

  useEscPress(() => {
    if (isOpen) onClose();
  });

  return (
    <div
      aria-hidden={!isOpen || null}
      className={cn(styles.root, { [styles.isOpen]: isOpen })}
      ref={element}
      /* We want the tab index here to focus trap inside the list on open */
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
      tabIndex="0"
    >
      <button
        className={cn(styles.close)}
        aria-label="Close"
        onClick={onClose}
        tabIndex="0"
      >
        <span aria-hidden>&times;</span>
      </button>
      <RefraccionesLogo className={cn(styles.refraccionesLogo)} />
      <BtgLogo className={cn(styles.btgLogo)} />

      <Text className={cn(styles.text)} text={text} />
    </div>
  );
};

export default AboutOverlay;
